<template>
  <Transition>
    <div v-if="showAlert" class="pe-alert flex" :class="currentType">
      <svg
        v-if="icon"
        class="mr-2 inline h-5 w-5 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clip-rule="evenodd"
        ></path>
      </svg>
      <div class="grow">
        <slot />
      </div>
      <button
        v-if="!unclosable"
        type="button"
        class="pe-alert--close -mx-1.5 -my-1.5 inline-flex h-8 w-8 cursor-pointer border-0 bg-transparent p-1.5"
        data-dismiss-target="#alert-1"
        aria-label="Close"
        @click="hide"
      >
        <span class="sr-only">Close</span>
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

const AlertTypes = ['primary', 'secondary', 'info', 'success', 'warning', 'danger']

const props = defineProps<{
  modelValue?: any
  type?: string
  icon?: boolean
  title?: string
  text?: string
  duration?: number
  unclosable?: boolean
}>()
const emit = defineEmits(['update:modelValue'])
const currentType = computed(() => {
  const type = props.type || 'primary'
  return AlertTypes.find((el) => el === type)
})

const showAlert = computed(() => props.modelValue || typeof props.modelValue === 'undefined')

const hide = () => {
  emit('update:modelValue', false)
}

onMounted(() => {
  if (props.duration) setTimeout(() => hide(), props.duration)
})
</script>

<style lang="scss" scoped>
.pe-alert {
  @apply mb-4 rounded-lg p-4 text-sm;
  .pe-alert--close {
    @apply text-inherit;
  }

  &.primary {
    @apply bg-neutral-900 text-neutral-300  dark:bg-neutral-800 dark:text-neutral-200;
    .pe-alert--close {
      @apply hover:text-neutral-500 dark:hover:text-neutral-400;
    }
  }
  &.secondary {
    @apply bg-neutral-100 text-neutral-700 dark:bg-neutral-200 dark:text-neutral-800;
    .pe-alert--close {
      @apply hover:text-neutral-800 dark:hover:text-neutral-900;
    }
  }
  &.info {
    @apply bg-blue-100 text-blue-700 dark:bg-blue-200 dark:text-blue-800;
    .pe-alert--close {
      @apply hover:text-blue-800 dark:hover:text-blue-900;
    }
  }
  &.success {
    @apply bg-green-100 text-green-700 dark:bg-green-200 dark:text-green-800;
    .pe-alert--close {
      @apply hover:text-green-800 dark:hover:text-green-900;
    }
  }
  &.warning {
    @apply bg-yellow-100 text-yellow-700 dark:bg-yellow-200 dark:text-yellow-800;
    .pe-alert--close {
      @apply hover:text-yellow-800 dark:hover:text-yellow-900;
    }
  }
  &.danger {
    @apply bg-red-100 text-red-700 dark:bg-red-200 dark:text-red-800;
    .pe-alert--close {
      @apply hover:text-red-800 dark:hover:text-red-900;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
